import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { RoleProtectedRoute } from '../components/RoleProtectedRoute';

// Páginas Públicas
import Login from '../pages/auth/Login';
import Register from '../pages/auth/Register';
import ForgotPassword from '../pages/auth/ForgotPassword';
import ResetPassword from '../pages/auth/ResetPassword';

// Páginas da Associação
import Dashboard from '../pages/Dashboard';
import Campaigns from '../pages/Campaigns';
import CampaignForm from '../pages/CampaignForm';
import CampaignDetail from '../pages/CampaignDetail';
import Draws from '../pages/Draws';
import DrawCreate from '../pages/DrawCreate';
import EditDraw from '../pages/EditDraw';
import DrawLive from '../pages/DrawLive';
import DrawWinners from '../pages/DrawWinners';
import PrizeList from '../pages/PrizeList';
import PrizeForm from '../pages/PrizeForm';
import SponsorList from '../pages/SponsorList';
import SponsorForm from '../pages/SponsorForm';
import StoreList from '../pages/StoreList';
import UserList from '../pages/UserList';
import StoreForm from '../pages/StoreForm';
import CouponList from '../pages/CouponList';
import CouponForm from '../pages/CouponForm';
import CouponBatchList from '../pages/CouponBatchList';
import CouponBatchForm from '../pages/CouponBatchForm';
import Reports from '../pages/Reports';
import ResetUserPassword from '../pages/ResetUserPassword';
import EditProfile from '../pages/EditProfile';
import HomePage from '../pages/HomePage';

// Páginas do Usuário Final
import LiveDrawSelector from '../pages/LiveDrawSelector';
import Winners from '../pages/Winners';
import UserCampaignList from '../pages/UserCampaignList';
import UserCampaignDetail from '../pages/UserCampaignDetail';
import RegisterUser from '../pages/RegisterUser';
import MyCoupons from '../pages/MyCoupons';
import MyCouponForm from '../pages/MyCouponFormSimple'; // Versão simplificada
import EditMyCoupon from '../pages/EditMyCoupon';
import CouponFormWithUserSelection from '../pages/CouponFormWithUserSelection';

// Sorteio de Lojas
import StoreDrawList from '../pages/StoreDrawList';
import StoreDrawCreate from '../pages/StoreDrawCreate';
import StoreDrawLive from '../pages/StoreDrawLive';

// Outras páginas
import NotFound from '../pages/NotFound';
import Error500 from '../pages/Error500';
import ErrorGeneric from '../pages/ErrorGeneric';

const AppRoutes: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div>Carregando...</div>;
  }

  return (
    <Routes>
      {/* ================================
          ROTAS PÚBLICAS
          ================================ */}
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" /> : <Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/register-user" element={<RegisterUser />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* ================================
          ROTAS DA ASSOCIAÇÃO
          ================================ */}
      <Route path="/dashboard" element={
        <RoleProtectedRoute allowedRoles={['SUPER_ADMIN', 'ASSOCIACAO', 'USUARIO_FINAL']}>
          <Dashboard />
        </RoleProtectedRoute>
      } />

      {/* Campanhas */}
      <Route path="/campaigns" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <Campaigns />
        </RoleProtectedRoute>
      } />
      <Route path="/campaigns/new" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <CampaignForm />
        </RoleProtectedRoute>
      } />
      <Route path="/campaigns/:id" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <CampaignDetail />
        </RoleProtectedRoute>
      } />
      <Route path="/campaigns/:id/edit" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <CampaignForm />
        </RoleProtectedRoute>
      } />

      {/* Sorteios */}
      <Route path="/draws" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO', 'USUARIO_FINAL']}>
          <Draws />
        </RoleProtectedRoute>
      } />
      <Route path="/draws/create" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <DrawCreate />
        </RoleProtectedRoute>
      } />
      <Route path="/draws/create/:campaignId" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <DrawCreate />
        </RoleProtectedRoute>
      } />
      <Route path="/draws/:id/edit" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <EditDraw />
        </RoleProtectedRoute>
      } />
      <Route path="/draws/live" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <LiveDrawSelector />
        </RoleProtectedRoute>
      } />
      <Route path="/draws/:id/live" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO', 'USUARIO_FINAL']}>
          <DrawLive />
        </RoleProtectedRoute>
      } />
      <Route path="/draws/:id/winners" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO', 'USUARIO_FINAL']}>
          <DrawWinners />
        </RoleProtectedRoute>
      } />

      {/* Prêmios */}
      <Route path="/prizes" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <PrizeList />
        </RoleProtectedRoute>
      } />
      <Route path="/prizes/new" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <PrizeForm />
        </RoleProtectedRoute>
      } />
      <Route path="/prizes/:id/edit" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <PrizeForm />
        </RoleProtectedRoute>
      } />

      {/* Patrocinadores */}
      <Route path="/sponsors" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <SponsorList />
        </RoleProtectedRoute>
      } />
      <Route path="/sponsors/new" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <SponsorForm />
        </RoleProtectedRoute>
      } />
      <Route path="/sponsors/:id/edit" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <SponsorForm />
        </RoleProtectedRoute>
      } />

      {/* Lojas */}
      <Route path="/stores" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <StoreList />
        </RoleProtectedRoute>
      } />
      <Route path="/stores/new" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <StoreForm />
        </RoleProtectedRoute>
      } />
      <Route path="/stores/:id/edit" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <StoreForm />
        </RoleProtectedRoute>
      } />

      {/* Usuários finais */}
      <Route path="/users" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <UserList />
        </RoleProtectedRoute>
      } />

      {/* Cupons */}
      <Route path="/coupons" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <CouponList />
        </RoleProtectedRoute>
      } />
      <Route path="/coupons/new" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <CouponForm />
        </RoleProtectedRoute>
      } />
      <Route path="/coupons/new-with-user" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <CouponFormWithUserSelection />
        </RoleProtectedRoute>
      } />

      {/* Lotes de Cupons */}
      <Route path="/coupon-batches" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <CouponBatchList />
        </RoleProtectedRoute>
      } />
      <Route path="/coupon-batches/new" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <CouponBatchForm />
        </RoleProtectedRoute>
      } />
      <Route path="/coupon-batches/:id/edit" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <CouponBatchForm />
        </RoleProtectedRoute>
      } />

      {/* Sorteio de Lojas */}
      <Route path="/store-draws" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <StoreDrawList />
        </RoleProtectedRoute>
      } />
      <Route path="/store-draws/new" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <StoreDrawLive />
        </RoleProtectedRoute>
      } />

      {/* Relatórios */}
      <Route path="/reports" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <Reports />
        </RoleProtectedRoute>
      } />

      {/* Perfil e Configurações */}
      <Route path="/profile" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO', 'USUARIO_FINAL']}>
          <EditProfile />
        </RoleProtectedRoute>
      } />
      <Route path="/reset-user-password" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <ResetUserPassword />
        </RoleProtectedRoute>
      } />
      <Route path="/reset-password-user/:id" element={
        <RoleProtectedRoute allowedRoles={['ASSOCIACAO']}>
          <ResetUserPassword />
        </RoleProtectedRoute>
      } />

      {/* ================================
          ROTAS DO USUÁRIO FINAL
          ================================ */}
      {/* Rotas com prefixo /user/ */}
      <Route path="/user/live-draws" element={
        <RoleProtectedRoute allowedRoles={['USUARIO_FINAL']}>
          <LiveDrawSelector />
        </RoleProtectedRoute>
      } />
      <Route path="/user/campaigns" element={
        <RoleProtectedRoute allowedRoles={['USUARIO_FINAL']}>
          <UserCampaignList />
        </RoleProtectedRoute>
      } />
      <Route path="/user/campaigns/:id" element={
        <RoleProtectedRoute allowedRoles={['USUARIO_FINAL']}>
          <UserCampaignDetail />
        </RoleProtectedRoute>
      } />
      <Route path="/user/my-coupons" element={
        <RoleProtectedRoute allowedRoles={['USUARIO_FINAL']}>
          <MyCoupons />
        </RoleProtectedRoute>
      } />
      <Route path="/user/my-coupons/new" element={
        <RoleProtectedRoute allowedRoles={['USUARIO_FINAL']}>
          <MyCouponForm />
        </RoleProtectedRoute>
      } />
      <Route path="/user/my-coupons/:id/edit" element={
        <RoleProtectedRoute allowedRoles={['USUARIO_FINAL']}>
          <EditMyCoupon />
        </RoleProtectedRoute>
      } />

      {/* Rotas sem prefixo (compatibilidade) */}
      <Route path="/live-draws" element={
        <RoleProtectedRoute allowedRoles={['USUARIO_FINAL']}>
          <LiveDrawSelector />
        </RoleProtectedRoute>
      } />
      <Route path="/my-coupons" element={
        <RoleProtectedRoute allowedRoles={['USUARIO_FINAL']}>
          <MyCoupons />
        </RoleProtectedRoute>
      } />
      <Route path="/my-coupons/new" element={
        <RoleProtectedRoute allowedRoles={['USUARIO_FINAL']}>
          <MyCouponForm />
        </RoleProtectedRoute>
      } />
      <Route path="/my-coupons/new/:id" element={
        <RoleProtectedRoute allowedRoles={['USUARIO_FINAL']}>
          <MyCouponForm />
        </RoleProtectedRoute>
      } />
      <Route path="/my-coupons/:id/edit" element={
        <RoleProtectedRoute allowedRoles={['USUARIO_FINAL']}>
          <EditMyCoupon />
        </RoleProtectedRoute>
      } />
      
      <Route path="/winners" element={<Winners />} />

      {/* ================================
          ROTAS DE ERRO
          ================================ */}
      <Route path="/error/500" element={<Error500 />} />
      <Route path="/error/generic" element={<ErrorGeneric />} />

      {/* ================================
          ROTA 404
          ================================ */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;
